import React from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { uniq } from 'lodash';
import { Link as RouterLink } from 'react-router-dom';
import { ColumnsType } from 'antd/es/table';
import { DOCUMENT_TYPE_OPTIONS } from 'utils';
import { Table, Text, Popconfirm, Link, Select } from 'components';
import DocumentsContext from './DocumentsContext';
import DocumentTag from './DocumentTag';
import ManageDropdown from './ManageDropdown';
import FileTypeIcon from './FileTypeIcon';
import ReplaceAgreementText from './ReplaceAgreementText';
import { DocumentType } from './types';

const DocumentsTable: React.FC = () => {
  const {
    selectedRowKeys,
    setSelectedRowKeys,
    documents,
    setDocumentType,
    currentTab,
    listing,
    listingAgreement,
  } = React.useContext(DocumentsContext);
  const [docTypeConfirmationOpen, setDocTypeConfirmationOpen] =
    React.useState<string>();

  const handleChangeDocType = (
    value: DocumentType['documentType'],
    id: string,
  ) => {
    setDocumentType(value, id);
    setDocTypeConfirmationOpen(null);
  };

  const PreviewLink: React.FC<{ id: string }> = ({ children, id }) => {
    const LinkWithProps = React.forwardRef<HTMLLinkElement, any>(
      ({ href }, ref) => (
        <Link ref={ref} color="textPrimary" underlineOnHover href={href}>
          {children}
        </Link>
      ),
    );
    return (
      <RouterLink
        to={`/listing/${listing.id}/documents/${id}`}
        component={LinkWithProps}
      />
    );
  };

  const columns: ColumnsType<(typeof documents)[number]> = [
    {
      title: 'Filename',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, { id }) => (
        <PreviewLink id={id}>{name}</PreviewLink>
      ),
    },
    {
      title: 'Document Type',
      dataIndex: 'documentType',
      key: 'documentType',
      render: (documentType, { id, isDeleted }) => {
        if (isDeleted) {
          return DOCUMENT_TYPE_OPTIONS.find(
            ({ value }) => value === documentType,
          ).label;
        }
        if (documentType === 'listingAgreement') {
          return <Text>Listing Paperwork</Text>;
        }
        return (
          <Popconfirm
            title={<ReplaceAgreementText />}
            width={320}
            open={docTypeConfirmationOpen === id}
            onConfirm={() => handleChangeDocType('listingAgreement', id)}
            onCancel={() => setDocTypeConfirmationOpen(null)}
            okText="Replace"
          >
            <div>
              <StyledDocTypeSelect
                value={documentType}
                listHeight={300}
                dropdownMatchSelectWidth={false}
                bordered={false}
                onChange={value => {
                  if (value === 'listingAgreement' && listingAgreement) {
                    return setDocTypeConfirmationOpen(id);
                  }
                  handleChangeDocType(value, id);
                }}
                options={DOCUMENT_TYPE_OPTIONS}
              />
            </div>
          </Popconfirm>
        );
      },
    },
    {
      title: 'FileType',
      dataIndex: 'fileType',
      key: 'fileType',
      render: (fileType, { id }) => (
        <PreviewLink id={id}>
          <FileTypeIcon fileType={fileType} />{' '}
          {fileType.toUpperCase().split('/')[1]}
        </PreviewLink>
      ),
    },
    {
      title: 'Submitted',
      dataIndex: 'submissionTime',
      key: 'submissionTime',
      render: submissionTime => moment(submissionTime).format('MMM D, YYYY'),
    },
    {
      title: 'Status',
      dataIndex: 'documentType',
      key: 'documentType',
      render: (documentType, { reason, isDeleted }) => (
        <DocumentTag
          status={listing.documentStatus}
          documentType={documentType}
          reason={reason}
          isDeleted={isDeleted}
        />
      ),
    },
    {
      title: 'Manage',
      dataIndex: 'id',
      key: 'manage',
      render: (id, { isDeleted, name, documentType }) => (
        <ManageDropdown
          id={id}
          isDeleted={isDeleted}
          name={name}
          documentType={documentType}
        />
      ),
    },
  ];

  const dataSource = documents
    .map(doc => ({ ...doc, key: doc.id }))
    .filter(({ isDeleted }) => {
      if (currentTab === 'all') return !isDeleted;
      return isDeleted;
    });

  return (
    <Table
      type="primary"
      rowSelection={{
        selectedRowKeys,
        getCheckboxProps: ({ status }) => ({
          disabled: status === 'accepted',
        }),
        onChange: (selected: string[]) => {
          // This fixes the check/uncheck all value, as the listingAgreement is hidden
          if (selected.length < selectedRowKeys.length) {
            const agreementIds = documents
              .filter(({ documentType }) => documentType === 'listingAgreement')
              .map(({ id }) => id);
            const filtered = selected.filter(id => !agreementIds.includes(id));
            setSelectedRowKeys(filtered);
          } else {
            const agreementIds = documents
              .filter(({ documentType }) => documentType === 'listingAgreement')
              .map(({ id }) => id);
            setSelectedRowKeys(uniq([...selected, ...agreementIds]));
          }
        },
        renderCell: (checked, { documentType }, i, originNode) => {
          if (documentType === 'listingAgreement') return null;
          return originNode;
        },
      }}
      pagination={false}
      dataSource={dataSource}
      columns={columns}
    />
  );
};

const StyledDocTypeSelect = styled(Select)`
  && .ant-select-selector {
    padding-left: 0;
  }
`;

export default DocumentsTable;
